import { useState, useEffect, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const POLL_INTERVAL_MS = 60000; // 1 min — reduz requisições/CPU com muitos usuários (socket atualiza em tempo real)
/** Rajada de mudanças de ticket: espera 1 s de silêncio, no máximo 3 s */
const COUNT_DEBOUNCE_MS = 1000;
const COUNT_MAX_WAIT_MS = 3000;

const useTicketCounts = ({ selectedQueueIds, showAll, isRefreshing }) => {
  const { user, socket } = useContext(AuthContext);
  const [ticketCounts, setTicketCounts] = useState({
    open: 0,
    pending: 0,
    group: 0,
    closed: 0,
    flow: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollTrigger, setPollTrigger] = useState(0);

  const isAdmin = user?.profile === "admin";
  const canSeeAllTickets = user?.allTicket === "enabled";
  const canSeeAllUserChats = user?.allUserChat === "enabled";
  const canAccessGroups = user?.allowGroup === true;
  const canSeeAllHistory = user?.allHistoric === "enabled";

  const effectiveShowAll =
    showAll &&
    (isAdmin || canSeeAllTickets || canSeeAllHistory || canSeeAllUserChats);

  const shouldIgnoreQueueFilter = effectiveShowAll;

  // Polling: atualiza contadores periodicamente para refletir mudanças (ex.: aba Aguardando) mesmo sem evento de socket
  useEffect(() => {
    const interval = setInterval(() => {
      setPollTrigger((t) => t + 1);
    }, POLL_INTERVAL_MS);
    // voltou para a aba: reconta
    const onVisible = () => {
      if (document.visibilityState === "visible") setPollTrigger((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Bolinha ao vivo: antes só o poll de 60 s atualizava — finalizar/aceitar deixava o número
  // errado por até 1 min. Reconta quando um ticket muda de status/atendente/fila (ou é
  // criado/apagado) e ao reconectar o socket; eventos só de mensagem (mesmo status) não
  // recontam, para não multiplicar /tickets/count (o backend daqui não tem cache).
  useEffect(() => {
    if (!socket || !user?.companyId) return undefined;
    const companyId = user.companyId;
    const lastSeen = new Map();
    let timer = null;
    let firstAt = 0;
    const schedule = () => {
      const now = Date.now();
      if (!firstAt) firstAt = now;
      if (timer) clearTimeout(timer);
      const wait = Math.max(0, Math.min(COUNT_DEBOUNCE_MS, firstAt + COUNT_MAX_WAIT_MS - now));
      timer = setTimeout(() => {
        timer = null;
        firstAt = 0;
        setPollTrigger((t) => t + 1);
      }, wait);
    };
    const onTicket = (data) => {
      if (data?.action === "create" || data?.action === "delete") {
        if (data?.ticket?.id) lastSeen.delete(data.ticket.id);
        schedule();
        return;
      }
      if (data?.action === "update" && data.ticket?.id) {
        const t = data.ticket;
        const key = `${t.status}|${t.userId ?? ""}|${t.queueId ?? ""}`;
        if (lastSeen.get(t.id) === key) return;
        if (lastSeen.size > 5000) lastSeen.clear();
        lastSeen.set(t.id, key);
        schedule();
      }
    };
    const onReconnect = () => setPollTrigger((t) => t + 1);
    socket.on(`company-${companyId}-ticket`, onTicket);
    socket.on("reconnect", onReconnect);
    return () => {
      socket.off(`company-${companyId}-ticket`, onTicket);
      socket.off("reconnect", onReconnect);
      if (timer) clearTimeout(timer);
    };
  }, [socket, user?.companyId]);

  useEffect(() => {
    let isMounted = true;
    const fetchTicketCounts = async () => {
      if (!isMounted) return;

      setLoading(true);
      setError(null);

      try {
        const validQueueIds = Array.isArray(selectedQueueIds)
          ? selectedQueueIds
          : [];

        let filteredQueueIds = validQueueIds;
        if (
          !isAdmin &&
          canSeeAllUserChats &&
          !canSeeAllHistory &&
          user?.queues
        ) {
          const userQueueIds = user.queues.map((q) => q.id);
          filteredQueueIds = validQueueIds.filter((id) =>
            userQueueIds.includes(id)
          );
        }

        const { data } = await api.get("/tickets/count", {
          params: {
            queueIds: JSON.stringify(filteredQueueIds),
            showAll: showAll ? "true" : "false",
          },
          timeout: 20000,
        });

        if (isMounted) {
          if (data && typeof data === "object") {
            const normalizedData = {
              open: parseInt(data.open) || 0,
              pending: parseInt(data.pending) || 0,
              group: parseInt(data.group) || 0,
              closed: parseInt(data.closed) || 0,
              flow: parseInt(data.flow) || 0,
            };

            if (!canAccessGroups) {
              normalizedData.group = 0;
            }

            setTicketCounts(normalizedData);
          } else {
            setError(new Error("Formato de dados inválido"));
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTicketCounts();

    return () => {
      isMounted = false;
    };
  }, [
    selectedQueueIds,
    showAll,
    isRefreshing,
    pollTrigger,
    canAccessGroups,
    effectiveShowAll,
    isAdmin,
    canSeeAllUserChats,
    canSeeAllHistory,
    user,
  ]);

  return {
    ticketCounts,
    loading,
    error,
    permissions: {
      isAdmin,
      canSeeAllTickets,
      canSeeAllUserChats,
      canAccessGroups,
      canSeeAllHistory,
      effectiveShowAll,
      shouldIgnoreQueueFilter,
    },
  };
};

export default useTicketCounts;

